<template>
  <section aria-labelledby="assignment-info" class="stack vertical">
    <h2
      v-if="name"
      id="assignment-info"
      class="flex-1 flex justify-between gap-3 flex-wrap items-center text-sm font-bold tracking-wide leading-none text-gray-600 mb-2"
    >
      <s-link
        :href="
          route('courses.assignments.show', {
            course: course.id,
            assignment: assignment?.id,
          })
        "
        class="hover:text-blue-600 flex items-center gap-1"
      >
        <s-icon name="book-edit" size="16" class="opacity-70" />
        {{ name }}
      </s-link>
    </h2>
    <!-- TODO: Add support for multiple start and due dates as seen on StaffAssignmentCard -->
    <!-- <div class="flex flex-wrap gap-2 mb-1">
      <s-label-value class="flex-1 whitespace-nowrap">
        <template #label>
          <span class="flex items-center gap-1">
            {{ t('started') }}
          </span>
        </template>
        <template v-if="startDate">
          <s-date :date="startDate" />
        </template>
        <div v-else class="flex my-0.5">
          <s-badge color="red" icon="alert" size="sm">{{ t('notAssigned') }}</s-badge>
        </div>
      </s-label-value>
      <div class="flex-1">
        <s-label-value class="flex-1 whitespace-nowrap">
          <template #label>
            <span class="flex items-center gap-1">
              {{ t('dueOn') }}
            </span>
          </template>
          <template v-if="dueDate">
            <s-date :date="dueDate" />
          </template>
          <div v-else class="flex my-0.5">
            <s-badge color="red" icon="alert" size="sm">{{ t('notAssigned') }}</s-badge>
          </div>
        </s-label-value>
      </div>
    </div> -->
    <div class="flex flex-wrap gap-2">
      <s-label-value :label="t('totalMarks')" class="flex-1">{{ totalMarks }}</s-label-value>
      <s-label-value v-if="precondition" :label="t('prerequisite')" class="flex-1">
        <s-tooltip>
          <template #tooltip>
            <div class="w-full max-w-[56ch]">
              <i18n-t keypath="prerequisiteTooltip">
                <template #assignment>
                  <strong>{{ precondition.prerequisiteAssignment.name }}</strong>
                </template>
                <template #grade>
                  <strong>{{ precondition.minimumGradePercentage }}</strong>
                </template>
              </i18n-t>
            </div>
          </template>
          {{ precondition.prerequisiteAssignment.name }}
        </s-tooltip>
      </s-label-value>
    </div>
    <div v-if="$slots.actions" class="mt-2">
      <slot name="actions" />
    </div>
  </section>
</template>
<script setup lang="ts">
import {computed} from 'vue';
import {isDatePast} from '../../../format/dates';
import SLabelValue from '../../../design-system/SLabelValue.vue';
import STooltip from '../../../design-system/STooltip.vue';
import {Assignment} from '../../../types/entities/assignment';
import {Course} from '../../../types/entities/course';
import SIcon from '../../../design-system/SIcon.vue';
import SLink from '../../../components/SLink.vue';
import {useI18n} from 'vue-i18n';
import {route} from 'ziggy-js';
import UserAssignmentTimerDto = App.DTOs.UserAssignmentTimerDto;

interface PreconditionDto {
  prerequisiteAssignment: {
    name: string;
  };
  minimumGradePercentage: number;
}

const props = defineProps<{
  assignment?: Assignment;
  name?: string;
  course: Course;
  startDate?: string;
  dueDate?: string;
  totalMarks: number;
  canGrantTimerExtensions?: boolean;
  timer?: UserAssignmentTimerDto | null;
  precondition?: PreconditionDto | null;
}>();

const isPastDueDate = computed(() => {
  if (!props.dueDate) return false;
  return isDatePast(props.dueDate);
});

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});
</script>
<i18n>
{
  "en": {
    "started": "Started",
    "notAssigned": "Not Assigned",
    "dueOn": "Due On",
    "totalMarks": "Total Marks",
    "prerequisite": "Prerequisite",
    "prerequisiteTooltip": "To access this assignment, the student will need to receive a grade of at least {grade}% on {assignment}."
  },
  "fr": {
    "started": "Commencé",
    "notAssigned": "Non attribué",
    "dueOn": "À rendre le",
    "totalMarks": "Notes totales",
    "prerequisite": "Prérequis",
    "prerequisiteTooltip": "Pour accéder à ce devoir, l'étudiant devra obtenir une note d'au moins {grade}% sur {assignment}."
  }
}
</i18n>
